import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export const PRESETS = {
  this_month: "This month",
  last_month: "Last month",
  last_quarter: "Last quarter",
  last_12_months: "Last 12 months",
  custom: "Custom range",
};

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
}

export function presetRange(preset, now = new Date()) {
  switch (preset) {
    case "this_month":
      return [startOfMonth(now), endOfMonth(now)];
    case "last_month": {
      const lm = addMonths(now, -1);
      return [startOfMonth(lm), endOfMonth(lm)];
    }
    case "last_quarter": {
      const m = now.getMonth();
      const q = Math.floor(m / 3);
      const lastQStartMonth = (q - 1) * 3;
      const start = new Date(now.getFullYear(), lastQStartMonth, 1);
      const end = new Date(now.getFullYear(), lastQStartMonth + 3, 0);
      return [start, end];
    }
    case "last_12_months": {
      const end = new Date(now);
      const start = new Date(now);
      start.setMonth(start.getMonth() - 11);
      return [startOfMonth(start), endOfMonth(end)];
    }
    default:
      return [startOfMonth(now), endOfMonth(now)];
  }
}

export function ymd(d) {
  if (!d) return "";
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
    x.getDate()
  ).padStart(2, "0")}`;
}

function DatePopover({ value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={testId}
          className={cn(
            "justify-start font-mono-num text-left text-sm border-zinc-300 hover:bg-zinc-100 w-full sm:w-44",
            !value && "text-zinc-500"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "dd-MMM-yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value || undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(d);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * DateRangeFilter
 *
 * Controlled component. Parent owns `preset` and `range = [from, to]` state.
 * - When preset !== "custom", it auto-derives `range` from the preset.
 * - Picking a date in From/To switches preset to "custom".
 *
 * Props:
 *   preset, setPreset, range, setRange, idPrefix
 */
export default function DateRangeFilter({
  preset,
  setPreset,
  range,
  setRange,
  idPrefix = "range",
}) {
  // Auto-recompute range when preset changes (skip "custom" so manual picks stick)
  useEffect(() => {
    if (preset !== "custom") {
      setRange(presetRange(preset));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const [minDate, maxDate] = range;

  return (
    <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
      <div>
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">
          Period
        </Label>
        <div className="mt-1.5">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger
              data-testid={`${idPrefix}-preset-select`}
              className="border-zinc-300 w-full sm:w-56"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`${idPrefix}-preset-${k}`}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">From</Label>
        <div className="mt-1.5">
          <DatePopover
            value={minDate}
            onChange={(d) => {
              if (!d) return;
              setPreset("custom");
              setRange(([, m]) => [d, m && d > m ? d : m]);
            }}
            placeholder="Start"
            testId={`${idPrefix}-min-date`}
          />
        </div>
      </div>

      <div>
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">To</Label>
        <div className="mt-1.5">
          <DatePopover
            value={maxDate}
            onChange={(d) => {
              if (!d) return;
              setPreset("custom");
              setRange(([f]) => [f && d < f ? d : f, d]);
            }}
            placeholder="End"
            testId={`${idPrefix}-max-date`}
          />
        </div>
      </div>

      <div className="lg:ml-auto flex flex-wrap gap-1.5">
        {["this_month", "last_month", "last_quarter", "last_12_months"].map((k) => (
          <Button
            key={k}
            size="sm"
            variant={preset === k ? "default" : "outline"}
            onClick={() => setPreset(k)}
            data-testid={`${idPrefix}-quick-${k}`}
            className={
              preset === k
                ? "bg-zinc-950 hover:bg-zinc-800 text-white h-8"
                : "border-zinc-300 hover:bg-zinc-100 h-8"
            }
          >
            {PRESETS[k]}
          </Button>
        ))}
      </div>
    </div>
  );
}
